/**
 * Login Page Component
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import LoginForm from '../components/auth/LoginForm';
import ForgotPasswordForm from '../components/auth/ForgotPasswordForm';
import NewPasswordForm from '../components/auth/NewPasswordForm';
import './LoginPage.css';

type AuthMode = 'login' | 'forgot-password' | 'new-password';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isInitialized, getPendingChallenge, clearError } = useAuth();
    const [authMode, setAuthMode] = useState<AuthMode>('login');

    // Check for pending challenges
    useEffect(() => {
        const challenge = getPendingChallenge();
        if (challenge?.challengeName === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            setAuthMode('new-password');
        }
    }, [getPendingChallenge]);

    // Also check for challenges when auth state changes
    useEffect(() => {
        const challenge = getPendingChallenge();
        if (challenge?.challengeName === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            setAuthMode('new-password');
        }
    }, [isInitialized, getPendingChallenge]);

    // OAuth callback is now handled automatically by Amplify
    // No need for manual handling

    // Redirect if already authenticated
    useEffect(() => {
        if (isInitialized && isAuthenticated) {
            const from = location.state?.from?.pathname || '/';
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, isInitialized, navigate, location]);

    // Show loading while initializing
    if (!isInitialized) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="loading-state">
                        <div className="spinner" />
                        <p>Initializing...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Don't render if already authenticated
    if (isAuthenticated) {
        return null;
    }

    const handleModeSwitch = (mode: AuthMode) => {
        clearError();
        setAuthMode(mode);
    };

    return (
        <div className="login-page">
            <div className="login-background">
                <div className="background-pattern" />
                <div className="background-gradient" />
            </div>

            <div className="login-container">
                {/* Features Section - Left Side */}
                <div className="features-section">
                    <div className="features-content">
                        {/* Branding */}
                        <div className="brand-section">
                            <h1 className="brand-title">AIサイトパトローラー</h1>
                            <p className="brand-subtitle">
                                AI-powered affiliate link monitoring and compliance checking
                            </p>
                        </div>

                        {/* Features List */}
                        <div className="features-list">
                            <div className="feature-item">
                                <div className="feature-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div className="feature-content">
                                    <h3>AI-Powered Detection</h3>
                                    <p>Advanced machine learning algorithms to identify affiliate links and sponsored content with high accuracy</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="feature-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div className="feature-content">
                                    <h3>Real-time Monitoring</h3>
                                    <p>Continuous monitoring of your content for compliance violations and policy changes as they happen</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="feature-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M9 19C9 20.1046 9.89543 21 11 21H13C14.1046 21 15 20.1046 15 19V18H9V19Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M3 5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V7C21 8.10457 20.1046 9 19 9H5C3.89543 9 3 8.10457 3 7V5Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M9 9V15"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M15 9V15"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div className="feature-content">
                                    <h3>Compliance Reports</h3>
                                    <p>Detailed reports and analytics to help you maintain compliance with advertising standards and regulations</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Auth Section - Right Side */}
                <div className="auth-section">
                    {authMode === 'login' && (
                        <LoginForm
                            onSwitchToForgotPassword={() => handleModeSwitch('forgot-password')}
                        />
                    )}

                    {authMode === 'forgot-password' && (
                        <ForgotPasswordForm
                            onSwitchToLogin={() => handleModeSwitch('login')}
                        />
                    )}

                    {authMode === 'new-password' && (
                        <NewPasswordForm
                            onComplete={() => {
                                // Redirect to intended page or dashboard
                                const from = location.state?.from?.pathname || '/';
                                navigate(from, { replace: true });
                            }}
                            onCancel={() => handleModeSwitch('login')}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
